import OptionConfirmModal, {
  OptionConfirmModalProps,
} from '@order/modals/OptionModals/OptionConfirmModal'
import OptionPackageVariantModal, {
  UpgradePackageVariant,
} from '@order/modals/OptionModals/OptionPackageVariantModal'
import OptionUpgradeModal from '@order/modals/OptionModals/OptionUpgradeModal'
import useBundleType from '@order/hooks/useBundleType'
import { useActiveDeal } from '@order/hooks/useDeal'
import useDealSettings from '@order/hooks/useDealSettings'
import { useOrderState } from '@order/hooks/useOrderState'
import { getStepOption, StepOption } from '@order/hooks/useOrderStep'
import { useNextPackageVariant } from '@order/hooks/usePackages'
import { SelectedItem } from '@order/orderTypes'
import {
  customizationPathname,
  endPathname,
  NextStep,
} from '@order/utils/getNextStepHref'
import { VariablesUtils } from '@order/utils/OrderVariables'
import { useSyncActiveItem } from '@screens/configuration/lenses/useSyncActiveItem'
import { router, useFocusEffect } from 'expo-router'
import Analytics from 'hooks/analytics/Analytics'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { parseStateParams } from '@order/hooks/useStepsState'
import { RecommendUtils } from 'recommend/RecommendUtils'
import ConfigDebugModal from 'features/debug/ConfigDebugModal'
import StepDebugModal from 'features/debug/StepDebugModal'
import RefractionDebugModal from 'features/debug/RefractionDebugModal'
import ColorsTemplate from './templates/Colors'
import DefaultTemplate from './templates/Default'
import ImageTilesTemplate from './templates/ImageTiles'
import TilesTemplate from './templates/Tiles'
import TintingTemplate from './templates/Tinting'
import PackagesAttributesTemplate from './templates/PackagesAttributes'
import useStepCtx from './useStepCtx'
import useStepHref from './useStepHref'
import useUpgradeItems, { isUpgradeItem } from './useUpgradeItems'

export default function Step() {
  const { t, i18n } = useTranslation(['steps', 'modals', 'deals-modals'])
  const ctx = useStepCtx()
  const { step, href, nextFrameHref } = useStepHref(ctx)
  const { bundleType } = useBundleType()

  const [upgradeModal, setUpgradeModal] = useState<{
    option: StepOption | null
    source: StepOption | null
    open: boolean
  }>({
    option: null,
    source: null,
    open: false,
  })

  const [packageUpgradeModal, setPackageUpgradeModal] = useState<{
    upgrade: UpgradePackageVariant | null
    option: StepOption | null
    open: boolean
  }>({
    upgrade: null,
    option: null,
    open: false,
  })

  const closePackageUpgradeModal = useCallback(
    () => setPackageUpgradeModal((s) => ({ ...s, open: false })),
    []
  )

  const getUpgradeItems = useUpgradeItems(step?.id || '')
  const config = useRemoteConfig()
  const deal = useActiveDeal()
  const activeItemId = useOrderState((s) => s.order.activeItemId)
  // const switchActiveItem = useOrderState((s) => s.switchActiveItem)
  const completeItem = useOrderState((s) => s.completeItem)
  const settings = useDealSettings()

  const statesRef = useRef(ctx.state.states)
  useEffect(() => {
    statesRef.current = ctx.state.states
  }, [ctx.state.states])

  useFocusEffect(
    useCallback(() => {
      const {
        order: { activeItemId },
        updateItem,
      } = useOrderState.getState()
      if (!activeItemId) {
        return
      }

      updateItem(activeItemId, statesRef.current, bundleType)
    }, [bundleType])
  )

  useSyncActiveItem()

  const [confirmModal, setConfirmModal] = useState<
    Omit<OptionConfirmModalProps, 'close'>
  >({
    onPress: undefined,
    open: false,
    title: '',
    desc: '',
    option: '',
    href: undefined,
  })

  const handleNext = useCallback(
    (next: NextStep, select: SelectedItem) => {
      const nextFrame = nextFrameHref(select, next)
      if (nextFrame?.href && nextFrame.activeItemId) {
        router.push(nextFrame.href)
        // switchActiveItem(nextFrame.nextItem.id)
        completeItem(nextFrame.activeItemId, nextFrame.states, bundleType)
        return
      }

      const isEnd =
        next.pathname === endPathname || next.pathname === customizationPathname

      const parsedParams = parseStateParams(next.params)

      // if (activeItemId && !isEnd) {
      //   updateItem(activeItemId, parsedParams, bundleType)
      // }

      if (activeItemId && isEnd) {
        completeItem(activeItemId, parsedParams, bundleType)
      }

      router.push(next)
    },
    [bundleType, completeItem, nextFrameHref, activeItemId]
  )

  const getNextPackageVariant = useNextPackageVariant()

  const options = useMemo(() => {
    if (!step) {
      return []
    }

    // Partially apply the href function
    const items = step.items
      .map((item) => {
        if (isUpgradeItem(item)) {
          return null
        }
        const select = {
          oId: `${item.id}`,
          vId: `${item.active.id}`,
          val: item.active.value,
          var: item.variable,
          action: item.action ?? undefined,
          o: VariablesUtils.toStateArray(item.active.overrides),
        } satisfies SelectedItem

        const next = href(step.itemNextStep, select)

        return {
          id: item.id,
          name: item.name,
          variantId: item.active.id,
          value: item.active.value,
          image: item.active.image,
          imageProps: item.active.imageProps,
          priceValue: item.active.priceValue,
          title: item.active.title,
          desc: item.active.desc,
          label: item.active.label,
          // Tl lookup for other translations
          lookup: item.active.lookup,
          pricePrefix: item.active.pricePrefix ?? settings.pricePrefix,
          priceDisplayOnly: item.active.priceDisplayOnly,
          priceSuffix: settings.priceSuffix,
          price: item.active.price,
          stepVariable: step.variable,
          // Prevent showing recommended items if avatar has recommended item
          recommended: !!item.active.recommended,
          // color: item.active.color,
          colors: item.active.colors,
          select,
          nextStep: step.itemNextStep,
          cta: item.active.cta,
          attributes: item.active.attributes,
          // href: href(step.itemNextStep, select),
          onPress: () => {
            const confirm = item.active.confirm
            const nextValues = {
              [item.variable]: item.active.value,
              ...VariablesUtils.fromOverrides(item.active.overrides),
            }
            const nextPackage = getNextPackageVariant(nextValues)
            if (nextPackage) {
              setPackageUpgradeModal({
                open: true,
                upgrade: nextPackage,
                option: item,
              })
              return
            }

            const upgradeItems = getUpgradeItems(item)
            if (upgradeItems.length > 0) {
              const upgrade = upgradeItems[0]
              setUpgradeModal({
                open: true,
                option: getStepOption(upgrade, {
                  stepId: step.id,
                  i18n,
                  deal,
                  config,
                  values: {
                    values: ctx.values,
                    refraction: ctx.refraction,
                  },
                }),
                source: item,
              })
              return
            }

            if (confirm?.result === false) {
              setConfirmModal({
                open: true,
                href: next,
                onPress: () => {
                  Analytics.logEvent('option_selected', {
                    stepId: `${step.id}`,
                    itemId: `${item.id}`,
                    variable: `${item.variable}`,
                    value: `${item.active.value}`,
                  })
                },
                title: confirm?.errorKey
                  ? t(`deals-modals:confirm.${confirm.errorKey}.title`)
                  : '',
                desc: confirm?.errorKey
                  ? t(`deals-modals:confirm.${confirm.errorKey}.desc`)
                  : '',
                option: t(
                  `steps:${item.variable}.items.${item.active.value}.title`
                ),
              })
              return
            }

            Analytics.logEvent('option_selected', {
              stepId: `${step.id}`,
              itemId: `${item.id}`,
              variable: `${item.variable}`,
              value: `${item.active.value}`,
            })

            if (!next) {
              return
            }

            handleNext(next, select)
          },
        }
      })
      .filter((item) => item !== null)

    const custom = step.customOptions.map((item) => ({
      id: item.id,
      // image: item.image?.src,
      name: item.name,
      title: item.title,
      desc: item.desc,
      price: item.price,
      pricePrefix: 'none',
      magicGlow: false,
      priceDisplayOnly: true,
      stepVariable: step.variable,
      nextStep: item.nextStep,
      select: {
        oId: `${item.id}`,
      },
      href: href(item.nextStep, {
        oId: `${item.id}`,
        action: item.action ?? undefined,
      }),
    }))

    return [...items, ...custom]
  }, [
    step,
    href,
    settings.priceSuffix,
    settings.pricePrefix,
    config,
    deal,
    getUpgradeItems,
    getNextPackageVariant,
    t,
    i18n,
    handleNext,
    ctx,
  ])

  const handleUpgradeClose = useCallback(
    () => setUpgradeModal((s) => ({ ...s, open: false })),
    []
  )

  const handleUpgradeOption = useCallback(
    (item: StepOption) => {
      if (!step) {
        return
      }

      const select = {
        oId: `${item.id}`,
        vId: `${item.active.id}`,
        val: item.active.value,
        var: item.variable,
        action: item.action ?? undefined,
        o: VariablesUtils.toStateArray(item.active.overrides),
      } satisfies SelectedItem
      const next = href(step.itemNextStep, select)
      if (!next) {
        return
      }
      handleNext(next, select)
    },
    [step, href, handleNext]
  )

  const closeConfirm = useCallback(
    () => setConfirmModal((s) => ({ ...s, open: false })),
    []
  )

  const handleUpgrade = useCallback(() => {
    if (!upgradeModal.option) {
      return
    }
    setUpgradeModal((s) => ({ ...s, open: false }))
    handleUpgradeOption(upgradeModal.option)
  }, [handleUpgradeOption, upgradeModal.option])

  const handleUpgradeSkip = useCallback(() => {
    if (!upgradeModal.source) {
      return
    }
    setUpgradeModal((s) => ({ ...s, open: false }))
    handleUpgradeOption(upgradeModal.source)
  }, [handleUpgradeOption, upgradeModal.source])

  const handlePackageUpgrade = useCallback(() => {
    if (!packageUpgradeModal.option) {
      return
    }
    setPackageUpgradeModal((s) => ({ ...s, open: false }))
    handleUpgradeOption(packageUpgradeModal.option)
  }, [handleUpgradeOption, packageUpgradeModal.option])

  const recommendations = useMemo(() => {
    if (!step?.variable) {
      return undefined
    }

    return RecommendUtils.getStepRecommendations(step, step.variable, {
      config: ctx.config,
      values: ctx.values,
      refraction: ctx.refraction,
      flowName: ctx.flowName,
    })
    // Do not use whole ctx as dep
  }, [ctx.flowName, step, ctx.config, ctx.values, ctx.refraction])

  const recommendedOption = useMemo(() => {
    return RecommendUtils.options.findRecommended(recommendations, options)
  }, [recommendations, options])

  if (!step) {
    return null
  }

  const desc = recommendedOption?.stepDesc ?? step.desc

  const template = step.template || 'default'

  return (
    <>
      {template === 'default' && (
        <DefaultTemplate
          title={step.title}
          desc={desc}
          options={options}
          recommendations={recommendations}
          variable={step.variable}
        />
      )}
      {template === 'image-tiles' && (
        <ImageTilesTemplate
          title={step.title}
          desc={desc}
          options={options}
          recommendations={recommendations}
          variable={step.variable}
        />
      )}
      {template === 'packages' && (
        <PackagesAttributesTemplate
          recommendations={recommendations}
          title={step.title}
          desc={desc}
          options={options}
        />
      )}
      {template === 'tiles' && (
        <TilesTemplate title={step.title} desc={desc} options={options} />
      )}
      {template === 'colors' && (
        <ColorsTemplate
          href={href}
          title={step.title}
          desc={desc}
          options={options}
        />
      )}
      {template === 'tinting' && (
        <TintingTemplate
          title={step.title}
          desc={desc}
          options={options}
          color={step.values['color']?.toString()}
        />
      )}
      <OptionUpgradeModal
        {...upgradeModal}
        onUpgrade={handleUpgrade}
        onClose={handleUpgradeClose}
        onSkip={handleUpgradeSkip}
      />
      <OptionConfirmModal close={closeConfirm} {...confirmModal} />
      <OptionPackageVariantModal
        {...packageUpgradeModal}
        onUpgrade={handlePackageUpgrade}
        onClose={closePackageUpgradeModal}
      />
      <ConfigDebugModal />
      <StepDebugModal step={step} ctx={ctx} />
      <RefractionDebugModal />
    </>
  )
}
